import React from 'react';
import { t, isLangLoaded } from '@/lib/i18n';
import { PROJECTION } from '@/constants/testIds';
import { isDebugMode } from '@/lib/debugMode';
import { noteWallError } from '@/lib/wallDiagnostics';

/**
 * WallErrorBoundary — LE FILET DU MUR.
 *
 * CE QU'IL FAUT DIRE D'ABORD, PARCE QUE C'EST UN RENSEIGNEMENT
 * ------------------------------------------------------------
 * `AppErrorBoundary` enveloppe DÉJÀ l'application entière (App.js), et une
 * erreur de rendu dans ProjectionConteScreen — y compris le rejet du chunk
 * paresseux — y serait bel et bien tombée. Elle aurait alors peint sa plaque
 * IVOIRE plein écran. Or l'écran du 12/08 était NOIR. Donc : l'écran noir du
 * propriétaire N'EST PAS une erreur de rendu React. C'est une information
 * qu'on obtient en lisant la frontière existante, pas en la remplaçant.
 *
 * ALORS POURQUOI CELLE-CI
 * -----------------------
 * Parce que « attrapé » et « diagnostiqué » sont deux choses. La plaque
 * ivoire est parfaite pour un invité et MUETTE pour un opérateur : elle ne
 * dit ni quel composant a lâché, ni avec quel message. Sur un mur qu'on
 * regarde à trois mètres, ou sur un téléphone qu'on tend à bout de bras
 * depuis le hall, c'est tout ce qui manquait. Cette frontière-ci :
 *
 *   · reste au vocabulaire DU MUR (plaque delft, filets d'or, ivoire) au
 *     lieu d'inonder de blanc une pièce sombre ;
 *   · dit à l'INVITÉ la même chose que partout ailleurs, dans sa langue
 *     (les clés `appError*`, présentes dans les sept dictionnaires) ;
 *   · dit à l'OPÉRATEUR, et seulement sous `?debug=1`, le message technique
 *     et la tête de la pile de composants ;
 *   · verse l'erreur au panneau de diagnostic (lib/wallDiagnostics.js), qui
 *     la montrera même si c'est LUI qu'on regarde.
 *
 * Elle est dans le bundle principal, au-dessus du `React.lazy` du mur :
 * un chunk qui ne se télécharge pas est donc attrapé ici, pas plus haut.
 */

/** Langue au mieux — la même que RouteFallback et AppErrorBoundary : l'état
 *  de langue de l'application est peut-être exactement ce qui a échoué. */
function bestEffortLang() {
    try {
        const u = new URLSearchParams(window.location.search).get('lang');
        // Une langue dont le dictionnaire est CHARGÉ, seulement (audit 10/09
        // bis, perf n°4 : un chunk par langue) — pour une autre, `t()`
        // répondrait en français ; autant le décider ici, une fois.
        if (u && isLangLoaded(u.toLowerCase())) return u.toLowerCase();
    } catch { /* URL illisible */ }
    try {
        const stored = window.localStorage.getItem('medard_lang');
        if (stored && isLangLoaded(stored)) return stored;
    } catch { /* navigation privée */ }
    if (typeof navigator !== 'undefined') {
        const code = (navigator.language || '').slice(0, 2).toLowerCase();
        if (isLangLoaded(code)) return code;
    }
    return 'fr';
}

export default class WallErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false, detail: '' };
        this.handleReload = this.handleReload.bind(this);
    }

    static getDerivedStateFromError(error) {
        let detail = '';
        try { detail = (error && (error.message || String(error))) || 'erreur sans message'; } catch { detail = 'erreur illisible'; }
        return { failed: true, detail: String(detail).slice(0, 400) };
    }

    componentDidCatch(error, info) {
        noteWallError('mur (rendu)', error);
        // Même trace que la frontière racine : jamais relancée vers l'invité.
        // eslint-disable-next-line no-console
        console.error('[medard] wall crash caught by boundary:', error, info);
        try {
            const stack = String(info?.componentStack || '').trim().split('\n').slice(0, 3).join(' ← ');
            if (stack) this.setState((s) => ({ detail: `${s.detail} · ${stack.trim()}` }));
        } catch { /* la pile est un bonus, jamais une dépendance */ }
    }

    handleReload() {
        try { window.location.reload(); } catch { /* rien d'autre à tenter */ }
    }

    render() {
        if (!this.state.failed) return this.props.children;

        const lang = bestEffortLang();
        return (
            <div
                data-testid={PROJECTION.crash}
                role="alert"
                className="fixed inset-0 z-[90] flex items-center justify-center bg-black px-6 text-center"
                style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                <div className="relative bg-manoir-delft-deep border border-manoir-gold/50 shadow-[0_50px_110px_-40px_rgba(0,0,0,0.95)] px-[clamp(1.5rem,7vw,7rem)] py-[clamp(1.5rem,5vh,4.5rem)] max-w-[86vw]">
                    {/* Le cartouche d'ex-libris — le double filet des plaques
                        de la porte d'entrée, pas un nouveau vocabulaire. */}
                    <span aria-hidden="true" className="absolute inset-2 border border-manoir-gold/50 pointer-events-none" />
                    <span aria-hidden="true" className="absolute inset-[13px] border border-manoir-gold/30 pointer-events-none" />
                    <div className="relative">
                        <h1 className="font-serif italic text-aman-ivory text-[clamp(1.3rem,3.6vh,2.7rem)] leading-tight">
                            {t(lang, 'appErrorTitle')}
                        </h1>
                        <p className="mt-[1.4vh] mx-auto max-w-[38ch] font-body text-[clamp(0.9rem,2vh,1.4rem)] leading-snug text-manoir-gold-soft">
                            {t(lang, 'appErrorBody')}
                        </p>
                        <button
                            type="button"
                            data-testid={PROJECTION.crashReload}
                            onClick={this.handleReload}
                            className="mt-[2.4vh] min-h-[44px] px-8 py-3 border border-manoir-gold bg-manoir-gold/10 text-aman-ivory font-body font-medium text-[clamp(0.7rem,1.5vh,1rem)] tracking-[0.28em] uppercase cursor-pointer"
                        >
                            {t(lang, 'appErrorReload')}
                        </button>
                        {/* L'aveu technique — pour l'opérateur seul. Une
                            frontière qui attrape sans dire quoi ne fait que
                            déplacer le silence. */}
                        {isDebugMode() && (
                            <p
                                data-testid={PROJECTION.crashDetail}
                                className="mt-[2vh] mx-auto max-w-[52ch] text-aman-ivory text-left"
                                style={{
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                                    fontSize: '0.85rem',
                                    lineHeight: '1.4',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {this.state.detail}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}
